import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import {
  Button,
  makeStyles,
  shorthands,
  tokens,
  Spinner,
  ProgressBar,
} from "@fluentui/react-components";
import { apiUrl } from "../utils/api";

const BRAND = {
  fontFamilyPrimary: "'Barlow', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  fontFamilyAccent: "'Playfair Display', Georgia, 'Times New Roman', serif",
  colors: {
    pageBg: "#361936",
    primaryPurple: "#4F266B",
    accent: "#F8AA00",
    accentHover: "#F8AA00",
    cardBg: "#fbf9fc",
    border: "#4F266B",
    textOnDark: "#B4AFB7",
    muted: "#B4AFB7",
    success: "#28a745",
  },
};

const useStyles = makeStyles({
  page: {
    minHeight: ["100vh", "100dvh"],
    backgroundColor: BRAND.colors.pageBg,
    backgroundImage:
      "radial-gradient(120% 120% at 85% 15%, #4F266B 0%, #3f1f57 28%, #361936 62%, #361936 100%)",
    color: tokens.colorNeutralForegroundInverted,
    fontFamily: BRAND.fontFamilyPrimary,
  },
  hero: {
    maxWidth: "1100px",
    marginLeft: "auto",
    marginRight: "auto",
    ...shorthands.padding("28px", "16px", "20px", "16px"),
    "@media (min-width: 768px)": {
      ...shorthands.padding("56px", "24px", "28px", "24px"),
    },
  },
  title: {
    marginTop: 0,
    marginBottom: "12px",
    color: BRAND.colors.accent,
    fontSize: "clamp(2rem, 8vw, 4rem)",
    lineHeight: 1.05,
    fontWeight: 400,
    fontFamily: BRAND.fontFamilyAccent,
  },
  subtitle: {
    marginTop: 0,
    marginBottom: "8px",
    color: BRAND.colors.textOnDark,
    fontSize: "clamp(0.95rem, 3.2vw, 1.15rem)",
    fontFamily: BRAND.fontFamilyPrimary,
  },
  formWrap: {
    maxWidth: "1100px",
    marginLeft: "auto",
    marginRight: "auto",
    ...shorthands.padding("8px", "16px", "28px", "16px"),
    "@media (min-width: 768px)": {
      ...shorthands.padding("16px", "24px", "40px", "24px"),
    },
  },
  formCard: {
    backgroundColor: BRAND.colors.cardBg,
    color: tokens.colorNeutralForeground1,
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.border("1px", "solid", BRAND.colors.border),
    boxShadow: tokens.shadow16,
    ...shorthands.padding("16px"),
    fontFamily: BRAND.fontFamilyPrimary,
    "@media (min-width: 768px)": {
      ...shorthands.padding("24px"),
    },
  },
  formTitle: {
    marginTop: 0,
    marginBottom: "8px",
    fontSize: "clamp(1.2rem, 4vw, 1.8rem)",
    color: BRAND.colors.pageBg,
    fontFamily: BRAND.fontFamilyAccent,
    fontWeight: 400,
  },
  formHint: {
    marginTop: 0,
    marginBottom: "24px",
    color: BRAND.colors.muted,
    fontFamily: BRAND.fontFamilyPrimary,
  },
  progressSection: {
    marginBottom: "32px",
  },
  progressLabel: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "8px",
    fontWeight: 600,
  },
  formList: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  formListItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px",
    backgroundColor: "rgba(0,0,0,0.02)",
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.border("1px", "solid", "rgba(0,0,0,0.05)"),
    "@media (max-width: 600px)": {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "12px",
    },
  },
  formInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  formName: {
    fontWeight: 700,
    fontSize: "1.1rem",
    color: BRAND.colors.pageBg,
  },
  formStatus: {
    fontSize: "0.9rem",
    color: BRAND.colors.muted,
  },
  formStatusSuccess: {
    fontSize: "0.9rem",
    color: BRAND.colors.success,
    fontWeight: 600,
  },
  actionButton: {
    minWidth: "120px",
    backgroundColor: BRAND.colors.accent,
    color: BRAND.colors.pageBg,
    border: `1px solid ${BRAND.colors.accent}`,
    fontFamily: BRAND.fontFamilyPrimary,
    fontWeight: 700,
    ":hover": {
      backgroundColor: BRAND.colors.accentHover,
      border: `1px solid ${BRAND.colors.accentHover}`,
      color: BRAND.colors.pageBg,
    },
  },
  stateCard: {
    maxWidth: "680px",
    margin: "48px auto",
    backgroundColor: BRAND.colors.cardBg,
    color: BRAND.colors.pageBg,
    ...shorthands.border("1px", "solid", BRAND.colors.border),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.padding("20px"),
    boxShadow: tokens.shadow8,
    textAlign: "center",
    fontFamily: BRAND.fontFamilyPrimary,
  },
});

type PublicFormType =
  | "contact"
  | "address"
  | "coverage"
  | "risikoanalyse"
  | "risikoanalyse-step"
  | "risikoanalyse-detail"
  | "risikokfz"
  | "risikokennzahlen"
  | "risikoimmobilie"
  | "hub";

interface ActivePublicForm {
  id: number;
  token: string;
  formType: PublicFormType;
  url: string;
  expiresAt: string;
  status: "NOT_FILLED" | "FILLED" | "APPLIED";
  submitted: boolean;
}

const getFormDisplayName = (formType: PublicFormType): string => {
  switch (formType) {
    case "risikoanalyse":
    case "risikoanalyse-step":
      return "Risikoanalyse (Basis)";
    case "risikoanalyse-detail":
      return "Risikoanalyse (Detailerfassung)";
    case "risikokfz":
      return "Risikodetails: KFZ";
    case "risikoimmobilie":
      return "Risikodetails: Immobilie";
    case "risikokennzahlen":
      return "Risikodetails: Kennzahlen";
    case "contact":
      return "Kundenformular";
    case "address":
      return "Adressprüfung";
    case "coverage":
      return "Deckungsprüfung";
    case "hub":
      return "Formular Übersicht";
    default:
      return "Kundenformular";
  }
};

const PublicCustomerFormHub = () => {
  const styles = useStyles();
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [forms, setForms] = useState<ActivePublicForm[]>([]);

  useEffect(() => {
    const fetchForms = async () => {
      if (!token) {
        setValid(false);
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(apiUrl(`public-form-hub/${token}/forms`));
        if (!res.ok) throw new Error("Invalid token");
        const data = await res.json();
        
        // Filter out the hub form itself so it doesn't appear in the list
        const formList = (data as ActivePublicForm[]).filter(
          (f) => f.formType !== "hub"
        );
        setForms(formList);
        setValid(true);
      } catch {
        setValid(false);
      } finally {
        setLoading(false);
      }
    };
    fetchForms();
  }, [token]);

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.stateCard}>
          <Spinner label="Übersicht wird geladen…" />
        </div>
      </div>
    );
  }

  if (!valid) {
    return (
      <div className={styles.page}>
        <div className={styles.stateCard}>
          Der Link ist ungültig oder abgelaufen.
        </div>
      </div>
    );
  }

  const completedCount = forms.filter((f) => f.status === "FILLED" || f.status === "APPLIED" || f.submitted).length;
  const totalCount = forms.length;
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <h1 className={styles.title}>Formular Übersicht</h1>
        <p className={styles.subtitle}>Herzlich Willkommen bei Courté.</p>
      </div>

      <div className={styles.formWrap}>
        <div className={styles.formCard}>
          <h2 className={styles.formTitle}>Ihre ausstehenden Aufgaben</h2>
          <p className={styles.formHint}>
            Hier finden Sie alle Formulare und Detailanalysen, die für Ihr Unternehmen vorbereitet wurden.
          </p>

          <div className={styles.progressSection}>
            <div className={styles.progressLabel}>
              <span>Fortschritt</span>
              <span>
                {completedCount} von {totalCount} abgeschlossen
              </span>
            </div>
            <ProgressBar value={progressPercent} max={100} />
          </div>

          <div className={styles.formList}>
            {forms.length === 0 ? (
              <p className={styles.formHint}>Aktuell liegen keine Formulare für Sie vor.</p>
            ) : (
              forms.map((form) => {
                const isCompleted = form.status === "FILLED" || form.status === "APPLIED" || form.submitted;
                return (
                  <div key={form.id} className={styles.formListItem}>
                    <div className={styles.formInfo}>
                      <span className={styles.formName}>{getFormDisplayName(form.formType)}</span>
                      {isCompleted ? (
                        <span className={styles.formStatusSuccess}>✔ Abgeschlossen</span>
                      ) : (
                        <span className={styles.formStatus}>Zur Bearbeitung ausstehend</span>
                      )}
                    </div>
                    {!isCompleted && (
                      <Button
                        className={styles.actionButton}
                        onClick={() => {
                          // The backend returns absolute URLs in form.url, but since we are a SPA,
                          // we can parse the pathname and navigate client-side to be faster.
                          try {
                            const urlObj = new URL(form.url);
                            navigate(urlObj.pathname);
                          } catch {
                            // Fallback if url is relative or unparseable
                            window.location.href = form.url;
                          }
                        }}
                      >
                        Starten
                      </Button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicCustomerFormHub;
